<template lang="pug">
  Row.estimated-delivery(align-items="stretch")
    Column.ed-status(
      mobile="12"
      tablet="6"
      desktop="6"
      :no-gutter="true"
    )
      .status(v-if="currentPackage.status === 'received' && emptyEDD")
        h4.center.label {{ __('Received') }}
        p.info.center {{ __('We received your order and it is now progressing through our system.') }}
      .status(v-else-if="currentPackage.status === 'processed' && emptyEDD")
        h4.center.label {{ __('Processed') }}
        p.info.center
          | {{ __('Your order has been processed and set-up for {shipmentType}', {shipmentType: currentPackage.isPickup ? 'pickup' : 'shipment'}) }}
          span(v-if="!currentPackage.isPickup") {{ __(' Shipping and tracking info is in processing.') }}
      .status(v-else-if="currentPackage.status === 'packed' && emptyEDD")
        h4.center.label {{ __('Shipment Picked') }}
        p.info.center {{ __('Your order has been picked and ready for QC.') }}
          span(v-if="!currentPackage.isPickup") {{ __(' Shipping and tracking info is in processing.') }}
      .status(v-else-if="currentPackage.status === 'quality' && emptyEDD")
        h4.label.center {{ __('Quality Checked') }}
        p.info.center.
          {{ __(
            'Your order has been checked for accuracy and is being prepared for {shipmentType}',
            {shipmentType: currentPackage.isPickup ? 'pickup' : 'shipment'}
          ) }}
      .status(v-else-if="currentPackage.status === 'prepared' && emptyEDD")
        h4.label.center {{ __('Preparing') }}
        p.info.center {{ __('Your order is being prepared for {shipmentType}', {shipmentType: currentPackage.isPickup ? 'pickup' : 'shipment'}) }}
      .status(v-else-if="currentPackage.status === 'transferred' && emptyEDD")
        h4.label.center {{ __('In Transit From U.S.') }}
        p.info.center {{ __('Your order is in transit from U.S.') }}
      .status(v-else-if="currentPackageEstimatedDeliveryDate && !pickupOrDelivered")
        .day-of-week {{ currentPackageEstimatedDeliveryDate.day_of_week }}
        .month {{ currentPackageEstimatedDeliveryDate.month }}
        .day {{ currentPackageEstimatedDeliveryDate.day }}
        .days-left(v-if="currentPackageEstimatedDeliveryDate.days_left > 0")
          | {{ currentPackageEstimatedDeliveryDate.days_left === 1 ? __('1 Day Left') : __('{count} Days Left', {count: currentPackageEstimatedDeliveryDate.days_left}) }}
        .days-left(v-else) {{ __('Will be Delivered Today')}}
      .status(v-else-if="isDeliveryRange")
        .day-of-week {{ __('Est Delivery') }}
        .estimated-range
          .date
            .month {{ currentPackageEstimatedDeliveryRange[0].month }}
            .day {{ currentPackageEstimatedDeliveryRange[0].day }}
          .separator -
          .date
            .month {{ currentPackageEstimatedDeliveryRange[1].month }}
            .day {{ currentPackageEstimatedDeliveryRange[1].day }}
      .status(
        v-else-if="currentPackage.status === 'shipped' \
          && !currentPackageEstimatedDeliveryDate \
          && currentPackage.estimatedDeliveryWrong \
          && !pickupOrDelivered"
      )
        h4.label.center {{ __('Shipped') }}
        p.info.center {{ __('Please contact us for an updated delivery date.') }}
      .status(v-else-if="currentPackage.status === 'shipped' && !currentPackage.isPickup && emptyEDD")
        h4.label.center {{ __('Shipped') }}
        p.info.center {{ __('Your order has been shipped and is in transit.') }}
      .status(v-else-if="currentPackage.status === 'shipped' && currentPackage.isPickup")
        h4.label.center {{ __('Ready For Pickup') }}
        p.info.center
          | {{__("Your order must be picked up within 2 weeks after it's ready for pickup. If your order is not picked up within that time frame, it will be canceled and you will be issued a refund.")}}
      .status(v-else-if="currentPackage.status === 'delivered' && deliveryDateFormatted")
        .day-of-week {{ deliveryDateFormatted.day_of_week }}
        .month {{ deliveryDateFormatted.month }}
        .day {{ deliveryDateFormatted.day }}
        .delivered {{ currentPackage.isPickup ? __('Picked Up') : __('Delivered') }}
      .status(v-else-if="currentPackage.packageType === 'virtual'")
        h4.center.label {{ __('Processing') }}
        p.info.center {{ __('Your digital product is being processed.') }}
    Column.ed-address(
      mobile="12"
      tablet="6"
      desktop="6"
      :no-gutter="true"
    )
      .delivery-address(v-if="deliveryAddress && !currentPackage.isPickup && currentPackage.packageType !== 'virtual'")
        h4.label.center {{ __('Delivery Address') }}
        p.info.center
          template(v-if="isAuthenticatedCustomersOrder")
            .name
              | {{ deliveryAddress.firstname?.toLowerCase() }} {{ deliveryAddress.lastname?.toLowerCase() }}

            .street-address-1
              | {{ deliveryAddress.street_1?.toLowerCase() }}
            .street-address-2(v-if="deliveryAddress.street_2")
              | {{ deliveryAddress.street_2?.toLowerCase() }}
          .city-state
            | {{ deliveryAddress.city?.toLowerCase() }}{{ displayRegion ? `, ${displayRegion}` : '' }} {{ isAuthenticatedCustomersOrder ? deliveryAddress.postcode : '' }}
            span(v-if='deliveryAddress.countryId !== "US"') {{ ` ${countryName}` }}
      .delivery-email(v-if="email && currentPackage.packageType === 'virtual' && (currentPackage.status === 'delivered' || currentPackage.deliveryDate)")
        h4.label.center {{ __('Delivered To') }}
        p.info.center {{ email }}
      pickup-address(v-if="currentPackage.isPickup")
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { FormattedDate, IRssDeliveryAddress, IRssPackage } from '../rssTypes'
import pickupAddress from './pickup-address.vue'
import { useConfigStore } from '@/js/stores/configStore'
import { useCustomerStore } from '@/js/stores/customerStore'

function formatDate(dateSource: string | Date | undefined): FormattedDate | null {
  if (dateSource) {
    const date = new Date(dateSource)

    return {
      day_of_week : date.toLocaleString('default', { weekday: 'long' }),
      month       : date.toLocaleString('default', { month: 'long' }),
      day         : date.getDate(),
      days_left   : Math.ceil((date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)),
    }
  }

  return null
}

const props = defineProps<{
  currentPackage: Partial<IRssPackage>,
  deliveryAddress: IRssDeliveryAddress | string,
  email: string,
}>()


const configStore = useConfigStore()
const customerStore = useCustomerStore()

const countries = computed(() => configStore.countries)
const deliveryAddressObj = computed(() => typeof props.deliveryAddress === 'object' ? props.deliveryAddress : undefined)
const countryName = computed(() => countries.value.find(c => c.alpha2 === deliveryAddressObj.value?.countryId)?.name || deliveryAddressObj.value?.countryId)
const displayRegion = computed(() => {
  const region = deliveryAddressObj.value?.region 
  
  return region && region !== '-' ? region : ''
})
  
const isDeliveryRange = computed<boolean>(() => {
  return !currentPackageEstimatedDeliveryDate.value
    && !!currentPackageEstimatedDeliveryRange.value.length
    && !pickupOrDelivered.value
})

const emptyEDD = computed<boolean>(() => !currentPackageEstimatedDeliveryDate.value || !props.currentPackage?.estimatedDeliveryWrong)

const pickupOrDelivered = computed<boolean>(() => props.currentPackage?.isPickup || props.currentPackage?.status === 'delivered')

const deliveryDateFormatted = computed<FormattedDate | null>(() => {
  const { deliveryDateFormatted: dateFormatted, deliveryDate } = props.currentPackage
  if (dateFormatted) {
    return dateFormatted
  }

  return formatDate(deliveryDate)
})

/**
 * Return current package estimated delivery date
 * @return {Object}
 */
const currentPackageEstimatedDeliveryDate = computed<FormattedDate | null>(() => {
  const {
    estimatedDeliveryTo,
    estimatedDeliveryFromFormatted,
    estimatedDeliveryFrom,
    estimatedDeliveryDateFormatted,
    estimatedDeliveryDate,
  } = props.currentPackage

  if (estimatedDeliveryDate) {
    return estimatedDeliveryDateFormatted || formatDate(estimatedDeliveryDate)
  }

  if (estimatedDeliveryFrom && !estimatedDeliveryTo) {
    return estimatedDeliveryFromFormatted || formatDate(estimatedDeliveryFrom)
  }

  return null
})

/**
 * Return current package estimated delivery date range
 * @return {Object}
 */
const currentPackageEstimatedDeliveryRange = computed<(FormattedDate | null)[]>(() => {
  const {
    estimatedDeliveryFrom,
    estimatedDeliveryTo,
    estimatedDeliveryFromFormatted,
    estimatedDeliveryToFormatted,
  } = props.currentPackage

  return (
    estimatedDeliveryFrom
    && estimatedDeliveryTo
  )
    ? [
      estimatedDeliveryFromFormatted || formatDate(estimatedDeliveryFrom),
      estimatedDeliveryToFormatted || formatDate(estimatedDeliveryTo),
    ]
    : []
})

const isAuthenticatedCustomersOrder = computed<boolean>(() => customerStore.loggedIn && customerStore.email === props.email)
</script>

<style lang="scss" scoped>
  .estimated-delivery {
    margin  : 0;
    padding : $space-s 0;

    &, .ed-status {
      border-color : $color-gray-light;
      border-style : solid;
      border-width : 0 0 1px 0;
    }

    .ed-status, .ed-address {
      padding: 0;
    }

    .ed-status {
      padding     : 0 0 $space-s 0;
      margin      : 0 0 $space-s 0;
      font-family : $font-condensed;
      font-size   : $size-s;

      .status {
        text-align : center;
      }

      .month {
        font-size   : $size-l;
        font-weight : bold;
      }

      .day {
        font-size   : $size-xxxxl;
        font-weight : bold;
      }
    }

    .info {
      font-size     : $size-xs;
      margin-bottom : 0;
    }

    .status, .delivery-address, .delivery-email {
      .label {
        font-size: $size-s;
      }
    }

    .delivery-address {
      .info {
        text-transform: capitalize;
      }
    }

    .delivery-email {
      .info {
        word-break: break-word;
      }
    }

    @media (min-width: $tablet) {
      padding : $space-s 0;

      .ed-status, .ed-address {
        padding : 0 $space-xxs $space-s;
        margin  : 0;
      }

      .ed-status {
        border-width : 0 1px 0 0;
      }
    }
  }
</style>
